import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { brandInfo } from '@/mocks/data';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');
  const [charCount, setCharCount] = useState(0);
  const siteUrl = 'https://dapigumanhua.com';
  const aboutUrl = `${siteUrl}/about`;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('消息已发送成功！我们会尽快回复您。');
      setFormData({ name: '', email: '', message: '' });
      setCharCount(0);
      return;
    }
    if (formData.message.length > 500) {
      setFormStatus('error');
      setFormMsg('留言内容不能超过 500 个字符');
      return;
    }
    const payload = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9qa9ksnlsngrm4llug0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('消息已发送成功！我们会尽快回复您。');
        setFormData({ name: '', email: '', message: '' });
        setCharCount(0);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        setFormStatus('error');
        setFormMsg(serverMsg || '发送失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: aboutUrl },
        ],
      },
      {
        '@type': 'Organization',
        name: brandInfo.name,
        url: siteUrl,
        description: brandInfo.description,
        foundingDate: '2024',
        contactPoint: {
          '@type': 'ContactPoint',
          email: brandInfo.contact.email,
          contactType: 'customer service',
        },
      },
      {
        '@type': 'AboutPage',
        name: '关于我们 - 打屁股漫画大全',
        description: brandInfo.description,
        url: aboutUrl,
      },
      {
        '@type': 'FAQPage',
        mainEntity: brandInfo.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 打屁股漫画大全"
        description="了解打屁股漫画大全的品牌故事与使命。我们致力于打造最全面的惩罚教育漫画内容平台，在欢笑中传递教育智慧。"
        keywords="关于我们,打屁股漫画,漫画平台介绍,漫画品牌"
        canonicalUrl={aboutUrl}
      />
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="relative w-full py-20 bg-background-100 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20a%20cozy%20comic%20reading%20corner%20with%20open%20books%20and%20manga%20pages%20scattered%2C%20soft%20watercolor%20style%2C%20warm%20cream%20and%20golden%20tones%2C%20Japanese-inspired%20aesthetic%2C%20gentle%20sunlight%20through%20window%2C%20peaceful%20and%20inviting%20atmosphere&width=1800&height=600&seq=about-hero-2026&orientation=landscape"
            alt="关于我们 - 打屁股漫画大全"
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">关于我们</h1>
            <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">{brandInfo.mission}</p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  品牌故事
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  {brandInfo.description}
                </p>
                <p className="text-foreground-700 leading-relaxed mb-6">
                  自{brandInfo.founded}创立以来，我们始终坚持"在欢笑中传递教育智慧"的核心理念，已收录超过 <strong className="text-primary-600">{brandInfo.articlesCount.toLocaleString()}</strong> 篇精选漫画场景文章，服务了 <strong className="text-primary-600">{(brandInfo.readersCount / 10000).toFixed(1)} 万+</strong> 漫画爱好者。
                </p>
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <p className="font-heading text-3xl font-bold text-primary-600">{brandInfo.articlesCount.toLocaleString()}+</p>
                    <p className="text-sm text-foreground-500">精选文章</p>
                  </div>
                  <div className="text-center">
                    <p className="font-heading text-3xl font-bold text-accent-600">{(brandInfo.readersCount / 10000).toFixed(1)}万+</p>
                    <p className="text-sm text-foreground-500">累计读者</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20beautiful%20artistic%20illustration%20of%20a%20cozy%20reading%20space%20filled%20with%20manga%20books%20and%20comic%20art%20on%20walls%2C%20warm%20lighting%2C%20comfortable%20armchair%2C%20peaceful%20Japanese-inspired%20aesthetic%2C%20watercolor%20style%2C%20cream%20and%20soft%20golden%20tones&width=800&height=600&seq=about-brand-01&orientation=landscape"
                  alt="品牌故事 - 打屁股漫画大全"
                  title="打屁股漫画大全品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {brandInfo.features.map((feature, idx) => (
                <div key={idx} className="p-6 bg-background-50 rounded-lg border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
              读者口碑
            </h2>
            <p className="text-foreground-600 text-center mb-10">听听漫画爱好者们怎么说</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {brandInfo.testimonials.map((t, idx) => (
                <div key={idx} className="p-5 bg-background-50 border border-background-200/70 rounded-lg">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-sm ${i < t.rating ? 'ri-star-fill text-secondary-500' : 'ri-star-line text-foreground-300'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {brandInfo.faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between p-5 font-medium text-foreground-900 text-sm list-none">
                    <span className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </span>
                      {faq.question}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform ml-2 flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5 pl-14">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">联系我们</h2>
                <p className="text-foreground-600 mb-8 leading-relaxed">
                  有任何问题、建议或合作意向？欢迎通过以下方式联系我们，或者填写右侧表单，我们会尽快回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm text-foreground-900">{brandInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">办公地址</p>
                      <p className="text-sm text-foreground-900">{brandInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信公众号</p>
                      <p className="text-sm text-foreground-900">{brandInfo.contact.wechat}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <h3 className="font-heading font-bold text-foreground-900 mb-5">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute left-[-9999px] opacity-0 h-0 w-0 overflow-hidden pointer-events-none"
                  />
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                      placeholder="你的姓名"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                      placeholder="你的邮箱"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.length <= 500) {
                          setFormData((p) => ({ ...p, message: val }));
                          setCharCount(val.length);
                        }
                      }}
                      placeholder="请输入留言内容（500字以内）"
                      rows={4}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{charCount}/500</p>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  {formStatus !== 'idle' && (
                    <p className={`text-sm text-center ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                      {formMsg}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}